import { noop } from 'lodash'

export interface ContentData {
    uuid?: string
    image?: string
    img_main?: string
    resource_type: string
    public_id?: string
    file_format?: string
}

interface Transformations {
    [key: string]: any
}

const TRANSFORMATION_KEY = {
    QUALITY: 'quality',
    FORMAT: 'format',
    X: 'x',
    Y: 'y',
    ASPECT_RATIO: 'aspect_ratio',
    RADIUS: 'radius',
    START_OFFSET: 'start_offset',
    FLAG: 'flag',
}

const TRANSFORMATION = {
    [TRANSFORMATION_KEY.QUALITY]: 'q',
    [TRANSFORMATION_KEY.FORMAT]: 'f',
    [TRANSFORMATION_KEY.X]: 'x',
    [TRANSFORMATION_KEY.Y]: 'y',
    [TRANSFORMATION_KEY.ASPECT_RATIO]: 'ar',
    [TRANSFORMATION_KEY.RADIUS]: 'r',
    [TRANSFORMATION_KEY.START_OFFSET]: 'so',
    [TRANSFORMATION_KEY.FLAG]: 'fl',
}

export const buildCloudinaryUrl = (
    data: ContentData,
    transformations?: Transformations,
    withDefault = true,
): string => {
    const transformationsDefault = withDefault ? { quality: 'auto', format: 'auto' } : {}
    const transformationsWithDefault: Transformations = transformations
        ? { ...transformationsDefault, ...transformations }
        : transformationsDefault

    // cloudinary transformation https://cloudinary.com/documentation/transformation_reference
    let transform = ''
    const keys = Object.keys(transformationsWithDefault)
    for (let i = 0; i < keys.length; i++) {
        const key = keys[i]
        if (i > 0) {
            transform += ','
        }
        transform += `${TRANSFORMATION[key]}_${transformationsWithDefault[key]}`
    }
    transform += '/'

    const { resource_type: resourceType, public_id: publicId } = data
    return `https://res.cloudinary.com/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/${resourceType}/upload/${transform}${publicId}`
}

function sliceFile(file: any, start: number, end: number): Blob {
    // for old browsers
    const slice = file.mozSlice ? file.mozSlice : file.webkitSlice ? file.webkitSlice : file.slice ? file.slice : noop

    return slice.bind(file)(start, end)
}

export function convertToBytes(mb: number): number {
    return mb * 1024 * 1024
}

export interface FileSlice {
    data: Blob
    start: number
    end: number
    size: number
}

const DEFAULT_CHUNK_SIZE_IN_BYTES = convertToBytes(
    Number(process.env.REACT_APP_CLOUDINARY_CHUNK_SIZE ? process.env.REACT_APP_CLOUDINARY_CHUNK_SIZE : 20),
)

export function processFile(file: File, sliceSize = DEFAULT_CHUNK_SIZE_IN_BYTES): FileSlice[] {
    const size = file.size
    let start = 0
    let result: FileSlice[] = []
    for (let end = 0; end < size; start += sliceSize) {
        end = start + sliceSize
        if (end > size) {
            end = size
        }
        const slice = sliceFile(file, start, end)
        result = result.concat([
            {
                data: slice,
                start,
                end: end - 1,
                size,
            },
        ])
    }
    return result
}

export function parseFileSize(mb: number): string {
    const q = Math.floor(mb / 1024)
    return q > 0 ? `${Math.round((mb / 1024) * 100) / 100} GB` : `${mb} MB`
}
